import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BlockModal, Button } from '@/lib/components';
import { ArrowCircleIcon, LogoutIcon } from '@/lib/icon';
import { RocketBlockModalIcon } from '@/lib/icon/RocketBlockModalIcon';
import { FloatModal } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { useUserData } from '@/src/application/hooks/useUserData';
import { sendEvent } from '@/src/compat/iframeIntercom';
import configData from '@/src/config.json';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { IFreeTrialDemo } from '@/src/presentation/types/interfaces/IFreeTrialDemo';

interface Parameters {
  name?: string;
  email?: string;
  contractId?: string;
  companyId?: string;
  language?: string;
  plan?: string;
}

export const FreeTrialDemo = ({
  showExtendsDemoButton,
  setBlockModal,
}: {
  showExtendsDemoButton: boolean;
  setBlockModal: Dispatch<SetStateAction<IFreeTrialDemo>>;
}) => {
  const { t } = useTranslation();
  const service = useService();
  const [userData] = useUserData();

  const logoutHandler = useCallback(() => {
    sendEvent(configData.HOME.SIGN_OFF, {});
  }, []);

  const getBuyEmBlueUrl = useCallback((parameters: Parameters) => {
    /* cspell:disable */
    const URL = `https://buy.embluemail.com/FormShare?contacto=${parameters.name ?? ''}&email=${
      parameters.email ?? ''
    }&tel=&pais=&activo=0&contrato=${parameters.contractId ?? ''}&id_deal=${
      parameters.companyId ?? ''
    }0&lang=${parameters.language ?? ''}&plan=${parameters.plan ?? ''}&isZero=true`;
    return URL;
  }, []);

  const choosePlan = useCallback(() => {
    window.open(getBuyEmBlueUrl(userData));
  }, [getBuyEmBlueUrl, userData]);

  const setExtraDay = useCallback(async () => {
    await service?.SetExtraDaysForFreeTrialDemo();
    setBlockModal((prevState) => ({ ...prevState, isBlocked: false }));
    document.body.style.overflow = 'scroll';
  }, [service, setBlockModal]);

  useEffect(() => {
    window.parent.scroll({ top: 0, behavior: 'smooth' });
    document.body.style.overflow = 'hidden';
  }, []);

  return (
    <FloatModal>
      <BlockModal>
        <BlockModal.Body>
          <div className="h-full px-12">
            <Flex withGap gapSize="large">
              <div className="w-[90%] max-w-[300px]">
                <RocketBlockModalIcon />
              </div>
              <div>
                <BlockModal.Title>{t('MODAL_BLOCK_FREE_TRIAL_DEMO.Title')}</BlockModal.Title>
                <div className="h-full w-full text-[1.3em]">
                  <p className="contents w-11/12 text-[#919296]">
                    {t('MODAL_BLOCK_FREE_TRIAL_DEMO.Body')}
                  </p>
                  <span className="contents text-emblue-blue-dark"> emBlue </span>
                  <p className="contents text-[#919296]">
                    {' '}
                    {t('MODAL_BLOCK_FREE_TRIAL_DEMO.Body2')}
                  </p>
                  <p className="contents w-11/12 text-emblue-blue-dark">
                    {' '}
                    {t('MODAL_BLOCK_FREE_TRIAL_DEMO.Body3')}
                  </p>
                  <p className="contents text-[#919296]">
                    {' '}
                    {t('MODAL_BLOCK_FREE_TRIAL_DEMO.Body4')}
                  </p>
                </div>
              </div>
            </Flex>
          </div>
        </BlockModal.Body>
        <BlockModal.SideBar>
          <button
            className="absolute right-8 top-5 cursor-pointer hover:bg-slate-300"
            onClick={logoutHandler}
          >
            <LogoutIcon />
          </button>
          <div className="pt-2 text-center text-21 font-medium">
            {t('MODAL_BLOCK_FREE_TRIAL_DEMO.SubTitle')}
            <p className="text-emblue-blue-dark">emBlue</p>
          </div>
          <div className="w-64">
            <Button fullWidth Icon={<ArrowCircleIcon />} onAction={choosePlan}>
              {t('MODAL_BLOCK_FREE_TRIAL_DEMO.ChoosePlanBtn')}
            </Button>
          </div>
          <>
            {showExtendsDemoButton && (
              <button
                className="text-emblue-blue-dark underline"
                onClick={() => void setExtraDay()}
              >
                {t('MODAL_BLOCK_FREE_TRIAL_DEMO.ExtraDaysButton')}
              </button>
            )}
          </>
        </BlockModal.SideBar>
      </BlockModal>
    </FloatModal>
  );
};
